// src/components/partner/ApiSettingsCard.tsx

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Key, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';

import { copyToClipboard } from '@utils/common';
import type { Partner, PartnerForm } from '@models/partner';

type SectionWrapperArgs = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  highlighted?: boolean;
  children: React.ReactNode;
};

type Props = {
  form: Partner;
  highlighted?: boolean;
  onChangeInput: (name: keyof PartnerForm, value: string | boolean | number | null) => void;
  onRegenerateAppId: () => void;
  onRegenerateAppSecret: () => void;
  sectionWrapper: (args: SectionWrapperArgs) => React.ReactElement;
};

const SECRET_HELP_TEXT =
  'Credentials are read-only. Rotate keys with Regenerate and update clients accordingly.';

const INPUT_HEIGHT_SM = 40; // MUI v5 small Outlined input height
const ACTION_COL_WIDTH_MD = 180;

/**
 * API Settings card
 * - App ID / App Secret are read-only (rotation happens via Regenerate)
 * - Copy / show actions are inline
 * - Callback URL is editable
 *
 * Layout note:
 * When a TextField has helperText, it increases the total height. To keep the
 * adjacent action button aligned, we reserve the same helperText space in the
 * button column using an invisible caption line.
 */
const ApiSettingsCard = ({
  form,
  highlighted,
  onChangeInput,
  onRegenerateAppId,
  onRegenerateAppSecret,
  sectionWrapper,
}: Props) => {
  const [showSecret, setShowSecret] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const closeToast = useCallback((_: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setToastMessage(null);
  }, []);

  const copyAndToast = useCallback((value: string, message: string) => {
    copyToClipboard(value);
    setToastMessage(message);
  }, []);

  const appId = form.appId ?? '';
  const appSecret = form.appSecret ?? '';

  const helperTextNode = useMemo(
    () => (
      <Typography variant="caption" color="text.secondary">
        {SECRET_HELP_TEXT}
      </Typography>
    ),
    [],
  );

  const helperTextSpacer = useMemo(
    () => (
      <Typography
        variant="caption"
        sx={{
          mt: '3px',
          lineHeight: 1.66,
          visibility: 'hidden',
        }}
      >
        {SECRET_HELP_TEXT}
      </Typography>
    ),
    [],
  );

  const actionColSx = useMemo(
    () => ({
      flexShrink: 0,
      minWidth: { xs: '100%', md: ACTION_COL_WIDTH_MD },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    }),
    [],
  );

  return sectionWrapper({
    title: 'API Settings',
    icon: <Key fontSize="small" />,
    highlighted,
    children: (
      <>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {/* App ID (read-only) + Regenerate */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="App ID"
                value={appId}
                size="small"
                fullWidth
                disabled
                helperText={helperTextNode}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => copyAndToast(appId, 'App ID copied')}
                          aria-label="Copy App ID"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={actionColSx}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={onRegenerateAppId}
                sx={{ whiteSpace: 'nowrap', height: INPUT_HEIGHT_SM }}
              >
                Regenerate
              </Button>
              {helperTextSpacer}
            </Box>
          </Stack>

          {/* App Secret (read-only, hidden by default) + Regenerate */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="App Secret"
                value={appSecret}
                size="small"
                fullWidth
                disabled
                type={showSecret ? 'text' : 'password'}
                helperText={helperTextNode}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showSecret ? 'Hide' : 'Show'}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setShowSecret((v) => !v)}
                          aria-label={showSecret ? 'Hide App Secret' : 'Show App Secret'}
                        >
                          {showSecret ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => copyAndToast(appSecret, 'App Secret copied')}
                          aria-label="Copy App Secret"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={actionColSx}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={onRegenerateAppSecret}
                sx={{ whiteSpace: 'nowrap', height: INPUT_HEIGHT_SM }}
              >
                Regenerate
              </Button>
              {helperTextSpacer}
            </Box>
          </Stack>

          {/* Callback URL (editable) */}
          <TextField
            label="Callback URL"
            value={form.callbackUrl ?? ''}
            size="small"
            fullWidth
            placeholder="https://..."
            onChange={(e) => onChangeInput('callbackUrl', e.target.value)}
          />
        </Stack>

        <Snackbar
          open={Boolean(toastMessage)}
          autoHideDuration={1800}
          onClose={closeToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeToast} severity="success" variant="filled">
            {toastMessage}
          </Alert>
        </Snackbar>
      </>
    ),
  });
};

export default ApiSettingsCard;
